import {
  ConfigurationStatus,
  FileParsingBatchStatus,
  FileProcessingStatus,
} from '../domain/enums';
import { OutcomeClass, ProviderHealth } from '../types';

/**
 * Route segments are relative to wherever the module is mounted. Building a
 * link or `navigate()` target always combines these with `useBasePath()`
 * rather than hardcoding an absolute path. See the module README's
 * portability rules.
 */
export const PARTNER_DATA_GATEWAY_ROUTES = {
  /** The module index is the provider overview; the list lives here. */
  ingestions: 'ingestions',
  ingestionDetail: 'ingestions/:eventId',
  /** `dataProvider` is an S3 folder name and is URL-encoded in links. */
  providerDetail: 'providers/:dataProvider',
  configurations: 'configurations',
  /**
   * Declared as a sibling of `configurationDetail`. React Router ranks a
   * static segment above a dynamic one, so `configurations/new` never
   * resolves as a configuration whose id is literally "new".
   */
  configurationCreate: 'configurations/new',
  configurationDetail: 'configurations/:configurationId',
} as const;

export const buildIngestionDetailPath = (eventId: string): string =>
  `ingestions/${eventId}`;

export const buildProviderDetailPath = (dataProvider: string): string =>
  `providers/${encodeURIComponent(dataProvider)}`;

/** Shared by the configuration detail and create pages' back controls. */
export const CONFIGURATIONS_BACK_LINK_LABEL = 'Back to configurations';

export const buildConfigurationDetailPath = (configurationId: string): string =>
  `configurations/${configurationId}`;

/** Matches the BFF's `ListIngestionsInput.limit` default. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * How many batches the Records tab's batch selector loads. `batchId` is a
 * required argument on `pdgIngestionRecords`, so the selector has to offer a
 * meaningful choice up front; one generous page keeps that to a single
 * request rather than paginating a dropdown.
 */
export const BATCH_SELECTOR_PAGE_SIZE = 100;

/**
 * Page size for the parsing tab's batch list. Each heatmap cell is only
 * ~24px, so a page this large still renders as a single glanceable grid, and
 * at the ~50MB chunk size PDGS uses it already covers a 5GB file.
 */
export const BATCH_PAGE_SIZE = 100;

export const FILE_PROCESSING_STATUS_LABELS: Record<
  FileProcessingStatus,
  string
> = {
  [FileProcessingStatus.Pending]: 'Pending',
  [FileProcessingStatus.Parsing]: 'Parsing',
  [FileProcessingStatus.Validating]: 'Validating',
  [FileProcessingStatus.Resolving]: 'Resolving',
  [FileProcessingStatus.Finalizing]: 'Finalizing',
  [FileProcessingStatus.Paused]: 'Paused',
  [FileProcessingStatus.Cancelled]: 'Cancelled',
  [FileProcessingStatus.Completed]: 'Completed',
  [FileProcessingStatus.CompletedWithErrors]: 'Completed with errors',
  [FileProcessingStatus.Failed]: 'Failed',
  [FileProcessingStatus.Rejected]: 'Rejected',
};

export const FILE_PARSING_BATCH_STATUS_LABELS: Record<
  FileParsingBatchStatus,
  string
> = {
  [FileParsingBatchStatus.Pending]: 'Pending',
  [FileParsingBatchStatus.Processing]: 'Processing',
  [FileParsingBatchStatus.Completed]: 'Completed',
  [FileParsingBatchStatus.Failed]: 'Failed',
  [FileParsingBatchStatus.Skipped]: 'Skipped',
};

export const CONFIGURATION_STATUS_LABELS: Record<ConfigurationStatus, string> =
  {
    [ConfigurationStatus.Draft]: 'Draft',
    [ConfigurationStatus.Published]: 'Published',
    [ConfigurationStatus.Deleted]: 'Deleted',
  };

/**
 * The configuration state machine PDGS enforces: `DRAFT → PUBLISHED →
 * DELETED`. There is no "unpublish" and no way back out of `DELETED`, so a
 * deleted configuration offers no transitions at all.
 */
export const CONFIGURATION_STATUS_TRANSITIONS: Record<
  ConfigurationStatus,
  ConfigurationStatus[]
> = {
  [ConfigurationStatus.Draft]: [
    ConfigurationStatus.Published,
    ConfigurationStatus.Deleted,
  ],
  [ConfigurationStatus.Published]: [ConfigurationStatus.Deleted],
  [ConfigurationStatus.Deleted]: [],
};

/** Verb for the button that performs each transition, not the state name. */
export const CONFIGURATION_STATUS_TRANSITION_LABELS: Record<
  ConfigurationStatus,
  string
> = {
  [ConfigurationStatus.Draft]: 'Move to draft',
  [ConfigurationStatus.Published]: 'Publish',
  [ConfigurationStatus.Deleted]: 'Delete',
};

export const CONTROL_ACTION_LABELS = {
  PAUSE: 'Pause',
  RESUME: 'Resume',
  CANCEL: 'Cancel',
} as const;

/**
 * Labels for the module's own DateRangePicker. Duplicated from Hinge
 * Connect's equivalent rather than imported: the module boundary rule
 * forbids reaching into a sibling ROI module, and the module must stay
 * self-contained so it can be extracted into a standalone app.
 */
export const DATE_RANGE_PICKER_LABELS = {
  startDate: 'Start Date',
  endDate: 'End Date',
  to: 'to',
} as const;

/**
 * Pipeline stages, in the order PDGS runs them.
 *
 * Source of truth is the PDGS domain type `StageHistoryEntry.stage`
 * (partner-data-gateway: features/file-ingestion/domain/types/
 * stage-history-entry.type.ts). PDGS already returns `stageHistory` sorted by
 * `startedAt` ascending, so this list is only used to render stages the
 * ingestion has not reached yet — it is a display skeleton, not a sort key.
 *
 * The BFF deliberately exposes `stage` as `String` rather than a GraphQL enum
 * so that a stage added by PDGS degrades gracefully instead of failing the
 * whole query. Anything not in this list is therefore still rendered, just
 * without a curated label.
 */
export const PIPELINE_STAGE_ORDER = [
  'S3_RECEIVED',
  'GATEKEEPER_ASSESSMENT',
  'WORKFLOW_STARTED',
  'PARSING',
  'VALIDATION',
  'IDENTITY_RESOLUTION',
  'COMPLETED',
] as const;

export type PipelineStage = (typeof PIPELINE_STAGE_ORDER)[number];

/**
 * Statuses PDGS reports for an individual pipeline stage. Distinct from
 * `FileProcessingStatus`, which describes the ingestion as a whole. Note
 * there is no "pending" — an entry is only appended once its stage starts.
 */
export const STAGE_STATUS = {
  InProgress: 'IN_PROGRESS',
  Success: 'SUCCESS',
  Failed: 'FAILED',
  Skipped: 'SKIPPED',
  Cancelled: 'CANCELLED',
} as const;

export type StageStatus = (typeof STAGE_STATUS)[keyof typeof STAGE_STATUS];

/**
 * Verdicts PDGS records on a validation report.
 *
 * Like stage names, the BFF exposes `PdgValidationReport.status` as an open
 * `String` rather than a GraphQL enum, so a verdict added later still
 * renders — just with a neutral banner instead of a curated colour.
 */
export const VALIDATION_STATUS = {
  Passed: 'PASSED',
  Failed: 'FAILED',
  Warning: 'WARNING',
} as const;

export type ValidationStatus =
  (typeof VALIDATION_STATUS)[keyof typeof VALIDATION_STATUS];

/**
 * Shared by the overview card and the assessments tab, so the same verdict
 * never reads two different ways.
 */
export const ASSESSMENT_MATCHED_LABEL = 'Matched';
export const ASSESSMENT_NOT_MATCHED_LABEL = 'No match';

/**
 * `liveProgressSource` values from PDGS. `db-final` means the workflow is
 * terminal and the database is authoritative; `unavailable` means the
 * Temporal query failed and progress should not be shown as stale.
 */
export const LIVE_PROGRESS_SOURCE = {
  TemporalQuery: 'temporal-query',
  DbFinal: 'db-final',
  Unavailable: 'unavailable',
} as const;

/**
 * `recordMapping.live.source` values from PDGS. `queue-query` means the
 * mapping queue was read and the counts beside it are real. Anything else
 * means the read failed, and PDGS zeroes every count — so those zeroes must
 * never be rendered as if they were measured.
 */
export const RECORD_MAPPING_LIVE_SOURCE = {
  QueueQuery: 'queue-query',
  Unavailable: 'unavailable',
} as const;

/**
 * Root of the internal Temporal Web UI. Kept as a constant so the deep links
 * on the diagnostics tab can be repointed (or made environment-driven) in one
 * place — the module has no access to the host app's config.
 */
export const TEMPORAL_UI_BASE_URL =
  'https://temporal-ui.internal.hingehealth.com';

/** Temporal namespace PDGS runs its ingestion workflows in. */
export const TEMPORAL_NAMESPACE = 'roi';

/** Deep link to a workflow's execution history in the Temporal Web UI. */
export const buildTemporalWorkflowUrl = (workflowId: string): string => {
  // Workflow ids are PDGS-generated and may contain path-unsafe characters.
  const encodedId = encodeURIComponent(workflowId);
  const namespacePath = `namespaces/${TEMPORAL_NAMESPACE}/workflows`;

  return `${TEMPORAL_UI_BASE_URL}/${namespacePath}/${encodedId}`;
};

export const PROVIDER_HEALTH_LABELS: Record<ProviderHealth, string> = {
  failing: 'Failing',
  inactive: 'Inactive',
  current: 'Current',
};

export const OUTCOME_CLASS_LABELS: Record<OutcomeClass, string> = {
  completed: 'Completed',
  partial: 'Partial',
  failed: 'Failed',
  rejected: 'Rejected',
  inFlight: 'In flight',
};

/**
 * Newest files fetched for one provider's detail page. PDGS caps a list
 * request at 100, so this is one request and the whole history the chart
 * and KPIs are computed from.
 */
export const PROVIDER_DETAIL_SAMPLE_SIZE = 100;
